import HttpClient from '../http/HttpClient';
import { LEAD_SERVICE_HOST, LEAD_API_PREFIX } from '../constants/RestConstants';

export interface CustomerDepositDTO {
  id: string;
  borrowerId: string;
  amount: number;
  currency: string;
  depositDate: string;
  maturityDate?: string;
  referenceNumber?: string;
  source?: string;
  notes?: string;
  status: string;
  closurePaymentMode?: string;
  closureComment?: string;
  createdBy?: string;
  createdDate?: string;
  updatedBy?: string;
  updatedDate?: string;
}

export interface CreateDepositPayload {
  amount: number;
  depositDate: string;
  maturityDate?: string;
  referenceNumber?: string;
  source?: string;
  notes?: string;
  /** Default true on server: email borrower if profile has email */
  notifyCustomer?: boolean;
}

export interface NotifyDepositEmailPayload {
  recipientEmail: string;
  message?: string;
}

export interface DepositSummaryResponse {
  totalActiveAmount: number;
  activeDepositCount: number;
  message: string;
  statusCode: number;
}

export interface DepositReportRow {
  depositId: string;
  borrowerId: string;
  borrowerName?: string;
  customerId?: string;
  amount: number;
  currency: string;
  depositDate: string;
  maturityDate?: string;
  referenceNumber?: string;
  source?: string;
  status: string;
  closurePaymentMode?: string;
  closureComment?: string;
  createdBy?: string;
  createdDate?: string;
}

export interface CloseDepositPayload {
  comment: string;
  paymentMode: string;
}

export interface DepositAuditEntry {
  id: string;
  depositId: string;
  action: string;
  summary?: string;
  previousAmount?: number;
  newAmount?: number;
  previousStatus?: string;
  newStatus?: string;
  performedBy?: string;
  performedAt?: string;
}

export async function createDeposit(borrowerId: string, payload: CreateDepositPayload) {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/borrowers/${borrowerId}/deposits`,
    data: payload,
  });
}

export async function listDepositsByBorrower(borrowerId: string): Promise<{ data?: CustomerDepositDTO[] }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/borrowers/${borrowerId}/deposits`,
  });
}

export async function getDepositsSummary(): Promise<{ data?: DepositSummaryResponse }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/deposits/summary`,
  });
}

export async function getDepositsReport(page = 0, size = 20) {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/deposits/report?page=${page}&size=${size}`,
  });
}

export async function getDepositAuditTrail(depositId: string): Promise<{ data?: DepositAuditEntry[] }> {
  return HttpClient.GET({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/deposits/${depositId}/audit`,
  });
}

export async function updateDeposit(
  depositId: string,
  body: { amount?: number; status?: string; notes?: string }
) {
  return HttpClient.PATCH({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/deposits/${depositId}`,
    data: body,
  });
}

/** Close an ACTIVE deposit (settlement payment mode + mandatory comment). */
export async function closeDeposit(depositId: string, payload: CloseDepositPayload) {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/deposits/${depositId}/close`,
    data: {
      comment: payload.comment,
      paymentMode: payload.paymentMode,
    },
  });
}

/** Send deposit summary to any email (optional message); audited on server. */
export async function notifyDepositByEmail(depositId: string, payload: NotifyDepositEmailPayload) {
  return HttpClient.POST({
    BASE_URL: LEAD_SERVICE_HOST,
    END_POINT: `${LEAD_API_PREFIX}/deposits/${depositId}/notify-email`,
    data: {
      recipientEmail: payload.recipientEmail,
      message: payload.message,
    },
  });
}
